/**
 * Хуки данных подписки/тарифа отеля для экрана «Подписка» владельца.
 *
 * Владелец: GET /v1/subscriptions/me (тариф/цена/статус/период), GET /v1/invoices
 * (счета подписки), POST /v1/invoices/{id}/pay (онлайн-оплата через Т-Банк →
 * pay-URL), POST /v1/subscriptions/provision (идемпотентное создание подписки,
 * если отель ещё не на тарифе — например после онбординга без подписки).
 *
 * Деньги в копейках (минорные единицы) — формат через formatMoney(.. / 100).
 * Реальное списание инициирует ТОЛЬКО владелец кнопкой «Оплатить»; авто-списаний
 * нет.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMySubscription,
  listInvoices,
  listRooms,
  listSubscriptionPlans,
  payInvoice,
  provisionSubscription,
} from '@hsc/api-client';
import type {
  Invoice,
  InvoiceListResponse,
  Payment,
  ProvisionSubscriptionRequest,
  Subscription,
  SubscriptionPlanListResponse,
} from '@hsc/types';

/** ApiError несёт числовой .status — нужен для отлова 404 «нет подписки». */
function statusOf(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'status' in err) {
    const s = (err as { status?: unknown }).status;
    return typeof s === 'number' ? s : undefined;
  }
  return undefined;
}

/** Текущая подписка отеля. 404 (нет подписки) НЕ ретраим. */
export function useMySubscription() {
  return useQuery<Subscription>({
    queryKey: ['subscription', 'me'],
    queryFn: getMySubscription,
    retry: (failureCount, err) => statusOf(err) !== 404 && failureCount < 2,
  });
}

/** Счета отеля (для блока «Счета подписки»). */
export function useInvoices(enabled = true) {
  return useQuery<InvoiceListResponse>({
    queryKey: ['invoices'],
    queryFn: listInvoices,
    enabled,
  });
}

/** Каталог тарифов (Старт/Резорт/Сеть) — для экрана «Сменить план». */
export function useSubscriptionPlans(enabled = true) {
  return useQuery<SubscriptionPlanListResponse>({
    queryKey: ['subscription', 'plans'],
    queryFn: listSubscriptionPlans,
    enabled,
  });
}

/** Кол-во номеров — нужно для расчёта/создания подписки. */
export function useRoomCount() {
  return useQuery<number>({
    queryKey: ['room-count'],
    queryFn: async () => (await listRooms({})).count,
  });
}

/** Онлайн-оплата счёта через Т-Банк. Возвращает Payment с tbank_payment_url. */
export function usePayInvoice() {
  const qc = useQueryClient();
  return useMutation<Payment, Error, { invoice: Invoice }>({
    mutationFn: ({ invoice }) =>
      payInvoice(invoice.id, `pay-${invoice.id}`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/** Идемпотентное создание подписки (владелец). */
export function useProvisionSubscription() {
  const qc = useQueryClient();
  return useMutation<Subscription, Error, ProvisionSubscriptionRequest>({
    mutationFn: provisionSubscription,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscription', 'me'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
